import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FC, memo, useCallback, useMemo } from 'react';
import { TruckDriverConstraintsSelector } from 'src/components/ui/shipmentOrders/TruckDriverConstraintsSelector';
import { VehicleAttributeOptionsState } from 'src/components/VehicleAttributeOptionsState';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { TruckDriverIdAndName } from 'src/entities/orderEntity';
import { ShipmentOrderLaneDetail } from 'src/entities/ShipmentOrderLane';
import {
  ShipmentOrderWaypointTimeEntity,
} from 'src/entities/ShipmentOrderResponse.entity';
import { VehicleAttributeCategory } from 'src/entities/VehicleAttributeValue.entity';
import { useDriversQuery } from 'src/hooks/useDrivers.query';
import { useQueryCompany } from 'src/hooks/useQueryCompany';
import { useTrucksQuery } from 'src/hooks/useTrucks.query';
import { useVehicleAttributeOptions } from 'src/hooks/useVehicleAttributeOptions';
import {
  buildCustomInputEntriesForDraft,
  collectCustomInputFieldKeys,
  mergeConstraintSelectionsByMasterOrder,
  mergeItemCanBeMixedLabel,
  mergeLegStatsDisplay,
  mergeUniqueFieldValues,
  mergeUniqueStrings,
} from 'src/utils/laneBasicInfoMerge';
import { getEarliestDateFormatted } from 'src/utils/shipmentLegRunItems';
import { VehicleAttributeOptionsOf } from 'src/utils/vehicleAttributeOptions';

import { buildLaneShippers } from './laneShippers';

import type { LaneBasicInfoDraft } from 'src/utils/buildLaneBasicInfoShipmentPatch';

type Props = {
  detail: ShipmentOrderLaneDetail;
  isEditing: boolean;
  startOn: string;
  companyId: number;
  basicInfoDraft: LaneBasicInfoDraft | null;
  setBasicInfoDraft: React.Dispatch<React.SetStateAction<LaneBasicInfoDraft | null>>;
  markBasicInfoDirty: () => void;
};

const WaypointTimesBlock: FC<{
  title: string;
  times: ShipmentOrderWaypointTimeEntity[];
  loadingOnFallback: string;
  isEditing: boolean;
  onChange: (next: ShipmentOrderWaypointTimeEntity[]) => void;
  onDirty: () => void;
}> = memo(({ title, times, loadingOnFallback, isEditing, onChange, onDirty }) => {
  const updateRow = useCallback((index: number, patch: Partial<ShipmentOrderWaypointTimeEntity>) => {
    const next = [...times];
    next[index] = { ...next[index], ...patch };
    onChange(next);
    onDirty();
  }, [times, onChange, onDirty]);

  const appendRow = useCallback(() => {
    if (times.length >= 3) return;
    onChange([...times, { startAt: '', endAt: '', stayingSeconds: null }]);
    onDirty();
  }, [times, onChange, onDirty]);

  const removeRow = useCallback((index: number) => {
    if (times.length <= 1) return;
    onChange(times.filter((_, i) => i !== index));
    onDirty();
  }, [times, onChange, onDirty]);

  return (
    <Box sx={{ gridColumn: '1 / -1' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>{title}</Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {times.map((field, index) => (
          <Box key={field.id ?? `row-${index}`} display="flex" alignItems="center" gap={2} flexWrap="wrap">
            {isEditing ? (
              <Box display="flex" gap={2} alignItems="center" flex={1}>
                <DatePicker
                  label="日付"
                  value={new Date(field.date ?? loadingOnFallback)}
                  onChange={(date) => {
                    const startTime = datetimeDecorator.toHourMinutes(new Date(field.startAt || loadingOnFallback));
                    const endTime = datetimeDecorator.toHourMinutes(new Date(field.endAt || loadingOnFallback));
                    const newDate = datetimeDecorator.toYyyyMmDd(new Date(date || loadingOnFallback));
                    updateRow(index, {
                      date: newDate,
                      startAt: startTime ? `${newDate}T${startTime}:00` : field.startAt,
                      endAt: endTime ? `${newDate}T${endTime}:00` : field.endAt,
                    });
                  }}
                  format="yyyy/MM/dd"
                  slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
                />
                <TextField
                  type="time"
                  label="開始時間"
                  size="small"
                  sx={{ flex: 1 }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={field.startAt ? datetimeDecorator.toHourMinutes(new Date(field.startAt)) : ''}
                  onChange={(e) => {
                    const dateValue = field.date ?? loadingOnFallback;
                    const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue));
                    updateRow(index, { startAt: `${currentDate}T${e.target.value}:00` });
                  }}
                />
                <Typography>〜</Typography>
                <TextField
                  type="time"
                  label="終了時間"
                  size="small"
                  sx={{ flex: 1 }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  value={field.endAt ? datetimeDecorator.toHourMinutes(new Date(field.endAt)) : ''}
                  onChange={(e) => {
                    const dateValue = field.date ?? loadingOnFallback;
                    const currentDate = datetimeDecorator.toYyyyMmDd(new Date(dateValue));
                    updateRow(index, { endAt: `${currentDate}T${e.target.value}:00` });
                  }}
                />
                <IconButton
                  size="small"
                  color="error"
                  disabled={times.length >= 3}
                  onClick={appendRow}
                >
                  <AddIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  disabled={times.length <= 1}
                  onClick={() => removeRow(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ) : (
              <Box display="flex" gap={1} alignItems="center">
                <Typography variant="body1" fontWeight="medium">
                  {datetimeDecorator.toYyyyMmDdHhMm(new Date(field.startAt))}
                </Typography>
                <Typography variant="body1">〜</Typography>
                <Typography variant="body1" fontWeight="medium">
                  {datetimeDecorator.toYyyyMmDdHhMm(new Date(field.endAt))}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
});

/**
 * レーン詳細 - 基本情報タブ（案件管理の伝票詳細と同構成。N 件伝票はマージ表示・編集はレーン単位ドラフト）
 */
/** 車両条件 7 カテゴリのドラフト上のキー。どれも string[] なので 1 つのハンドラで扱える */
type ConstraintDraftKey =
  | 'designatedTruckKlasses'
  | 'designatedTruckCarModels'
  | 'designatedTruckLoadingPlatformHeights'
  | 'designatedTruckLoadingPlatformWidths'
  | 'designatedTruckLoadingPlatformLengths'
  | 'designatedTruckFloorSpecifications'
  | 'designatedTruckFeatures';

const LaneDetailBasicInfo: FC<Props> = memo(({
  detail,
  isEditing,
  startOn,
  companyId,
  basicInfoDraft,
  setBasicInfoDraft,
  markBasicInfoDirty,
}) => {
  const orders = detail.shipmentOrders;
  // 荷主ごとにまとめる。同名でも担当者・連絡先が違えば別の相手として分ける
  const laneShippers = useMemo(() => buildLaneShippers(orders), [orders]);
  const { data: truckData } = useTrucksQuery(startOn, { enabled: !!startOn });
  const { data: driverData } = useDriversQuery(startOn, { enabled: !!startOn });
  const { data: companyData } = useQueryCompany(companyId);
  // 車両条件の選択肢と並び順はシステム設定「車両表示設定」のマスタ由来（#329 P8）
  const {
    optionsOf,
    isLoading: attributeOptionsAreLoading,
    isError: attributeOptionsFailed,
  } = useVehicleAttributeOptions(companyId);

  // issue #283: 任意項目・備考は編集ドラフトに持たず、閲覧専用に伝票(detail)から算出する。
  // 会社マスタの任意項目名 ∪ 各伝票のキーを集約し、キーごとに先頭伝票の値を代表表示する。
  const customInputEntries = useMemo(
    () => buildCustomInputEntriesForDraft(
      collectCustomInputFieldKeys(companyData?.custom_input_fields ?? [], orders),
      orders,
    ),
    [companyData, orders],
  );
  const memoDisplay = orders[0]?.memo ?? '';

  const trucksOptions = useMemo(() => (truckData ?? []).map((t) => ({
    id: t.id,
    name: t.licensePlateValue,
  })), [truckData]);

  const driversOptions = useMemo(() => (driverData ?? []).map((d) => ({
    id: d.id,
    name: d.name,
  })), [driverData]);

  const loadingNames = mergeUniqueStrings(orders.map((o) => o.shipmentOrderLoadingWaypoint.name));
  const loadingAddresses = mergeUniqueStrings(orders.map((o) => o.shipmentOrderLoadingWaypoint.address));
  const unloadingNames = mergeUniqueStrings(orders.map((o) => o.shipmentOrderUnloadingWaypoint.name));
  const unloadingAddresses = mergeUniqueStrings(orders.map((o) => o.shipmentOrderUnloadingWaypoint.address));

  const codesDisplay = mergeUniqueStrings(orders.map((o) => o.code));
  const registrantsDisplay = mergeUniqueStrings(
    orders.map((o) => o.registeredLicense?.licenseProfile?.personName || o.registeredLicense?.email || ''),
  );

  const allAllowedTruckIds = [...new Set(orders.flatMap((o) => o.allowedTruckIds ?? []))];
  const allDeniedDriverIds = [...new Set(orders.flatMap((o) => o.deniedDriverIds ?? []))];

  // 並びの正本は会社マスタ。マスタに無い現在値は mergeConstraintSelectionsByMasterOrder が末尾に残す（§5-4）
  const mergedDesignatedTruckKlasses = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(orders, (o) => o.designatedTruckKlasses, optionsOf('klass', [])),
    [orders, optionsOf],
  );
  const mergedDesignatedTruckCarModels = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(orders, (o) => o.designatedTruckCarModels, optionsOf('car_model', [])),
    [orders, optionsOf],
  );
  const mergedDesignatedTruckLoadingPlatformHeights = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformHeights,
      optionsOf('loading_platform_height', []),
    ),
    [orders, optionsOf],
  );
  const mergedDesignatedTruckLoadingPlatformWidths = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformWidths,
      optionsOf('loading_platform_width', []),
    ),
    [orders, optionsOf],
  );
  const mergedDesignatedTruckLoadingPlatformLengths = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckLoadingPlatformLengths,
      optionsOf('loading_platform_length', []),
    ),
    [orders, optionsOf],
  );
  const mergedDesignatedTruckFloorSpecifications = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(
      orders,
      (o) => o.designatedTruckFloorSpecifications,
      optionsOf('floor_specification', []),
    ),
    [orders, optionsOf],
  );
  const mergedDesignatedTruckFeatures = useMemo(
    () => mergeConstraintSelectionsByMasterOrder(orders, (o) => o.designatedTruckFeatures, optionsOf('features', [])),
    [orders, optionsOf],
  );

  const draftAllowedTrucks = useMemo((): TruckDriverIdAndName[] => {
    if (!basicInfoDraft) return [];
    return basicInfoDraft.allowedTruckIds
      .map((id) => trucksOptions.find((t) => t.id === id))
      .filter((t): t is TruckDriverIdAndName => t != null);
  }, [basicInfoDraft, trucksOptions]);

  /**
   * 車両条件のチェックを 1 つ切り替える。
   *
   * Why not カテゴリごとにハンドラを書く: 7 か所で同じ push/splice を書き写すことになり、
   * 1 か所だけ markBasicInfoDirty を落とすといった取りこぼしが起きる。
   */
  const toggleConstraint = useCallback((key: ConstraintDraftKey, option: string, checked: boolean) => {
    setBasicInfoDraft((p) => {
      if (!p) return p;
      const cur = [...(p[key] || [])];
      if (checked) {
        if (!cur.includes(option)) cur.push(option);
      } else {
        const i = cur.indexOf(option);
        if (i >= 0) cur.splice(i, 1);
      }
      return { ...p, [key]: cur };
    });
    markBasicInfoDirty();
  }, [setBasicInfoDraft, markBasicInfoDirty]);

  const draftDeniedDrivers = useMemo((): TruckDriverIdAndName[] => {
    if (!basicInfoDraft) return [];
    return basicInfoDraft.deniedDriverIds
      .map((id) => driversOptions.find((d) => d.id === id))
      .filter((d): d is TruckDriverIdAndName => d != null);
  }, [basicInfoDraft, driversOptions]);

  const handleChangeAllowedTrucks = useCallback(
    (_event: unknown, value: TruckDriverIdAndName[]) => {
      setBasicInfoDraft((p) => (p ? { ...p, allowedTruckIds: value.map((t) => t.id) } : p));
      markBasicInfoDirty();
    },
    [setBasicInfoDraft, markBasicInfoDirty],
  );

  const handleChangeDeniedDrivers = useCallback(
    (_event: unknown, value: TruckDriverIdAndName[]) => {
      setBasicInfoDraft((p) => (p ? { ...p, deniedDriverIds: value.map((d) => d.id) } : p));
      markBasicInfoDirty();
    },
    [setBasicInfoDraft, markBasicInfoDirty],
  );

  const loadingOnFallback = basicInfoDraft
    ? getEarliestDateFormatted(basicInfoDraft.loadingWaypointTimes)
    : getEarliestDateFormatted(orders[0]?.shipmentOrderLoadingWaypoint.shipmentOrderWaypointLoadingTimes ?? []);

  const unloadingOnFallback = basicInfoDraft
    ? getEarliestDateFormatted(basicInfoDraft.unloadingWaypointTimes)
    : getEarliestDateFormatted(orders[0]?.shipmentOrderUnloadingWaypoint.shipmentOrderWaypointUnloadingTimes ?? []);

  if (!basicInfoDraft) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">基本情報を読み込んでいます…</Typography>
        </CardContent>
      </Card>
    );
  }

  const draft = basicInfoDraft;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" mb={3}>基本情報</Typography>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            システム情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">伝票No</Typography>
              <Typography variant="body1" fontWeight="medium">{codesDisplay}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">案件登録者</Typography>
              <Typography variant="body1" fontWeight="medium">{registrantsDisplay}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">レーンID</Typography>
              <Typography variant="body1" fontWeight="medium">{detail.id}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">伝票数 / LEG数</Typography>
              <Typography variant="body1" fontWeight="medium">
                {detail.shipmentOrderCount}件 / {detail.legCount}件
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'blue.50', borderRadius: 2 }}>
          {/* 伝票ごとに 1 カードを縦に積むと、同じ荷主が伝票の数だけ並んで画面 1 枚分の
              幅と高さを取っていた。荷主ごとにまとめて 1 行で出す（担当・TEL は右寄せ） */}
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              荷主情報
            </Typography>
            <Box
              component="span"
              sx={{
                px: 0.75, borderRadius: 1, bgcolor: 'grey.200',
                fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary',
              }}
            >
              {`${laneShippers.length}社`}
            </Box>
          </Box>
          <Box sx={{ bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
            {laneShippers.map((shipper, index) => (
              <Box
                key={`${shipper.shipperName}-${shipper.contactText}`}
                sx={{
                  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                  gap: 2, px: 2, py: 1,
                  ...(index > 0 && { borderTop: '1px solid', borderColor: 'grey.100' }),
                }}
              >
                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 0 }}>
                  {shipper.shipperName}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  {shipper.contactText}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'indigo.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            区間情報
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">区間種別（積地 → 降地）</Typography>
          <Typography variant="body1" fontWeight="medium">{mergeLegStatsDisplay(orders)}</Typography>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'green.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            積地情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">積地名称</Typography>
              <Typography variant="body1" fontWeight="medium">{loadingNames}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">積地住所</Typography>
              <Typography variant="body1" fontWeight="medium">{loadingAddresses}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">積日</Typography>
              <Typography variant="body1" fontWeight="medium">{loadingOnFallback || '—'}</Typography>
            </Box>
            <WaypointTimesBlock
              title="時間枠"
              times={draft.loadingWaypointTimes}
              loadingOnFallback={loadingOnFallback || detail.loadingOn}
              isEditing={isEditing}
              onChange={(next) => setBasicInfoDraft((p) => (p ? { ...p, loadingWaypointTimes: next } : p))}
              onDirty={markBasicInfoDirty}
            />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={draft.loadingStayingMinutes === '' ? '' : draft.loadingStayingMinutes}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0;
                    setBasicInfoDraft((p) => {
                      if (!p) return p;
                      return { ...p, loadingStayingMinutes: v };
                    });
                    markBasicInfoDirty();
                  }}
                  slotProps={{
                    input: { endAdornment: <Typography variant="body2">分</Typography> },
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium">
                  {draft.loadingStayingMinutes === '' || draft.loadingStayingMinutes === undefined
                    ? '—'
                    : `${draft.loadingStayingMinutes}分`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'green.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            降地情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">降地名称</Typography>
              <Typography variant="body1" fontWeight="medium">{unloadingNames}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">降地住所</Typography>
              <Typography variant="body1" fontWeight="medium">{unloadingAddresses}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">降日</Typography>
              <Typography variant="body1" fontWeight="medium">{unloadingOnFallback || '—'}</Typography>
            </Box>
            <WaypointTimesBlock
              title="時間枠"
              times={draft.unloadingWaypointTimes}
              loadingOnFallback={unloadingOnFallback || detail.loadingOn}
              isEditing={isEditing}
              onChange={(next) => setBasicInfoDraft((p) => (p ? { ...p, unloadingWaypointTimes: next } : p))}
              onDirty={markBasicInfoDirty}
            />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">滞在時間</Typography>
              {isEditing ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={draft.unloadingStayingMinutes === '' ? '' : draft.unloadingStayingMinutes}
                  onChange={(e) => {
                    const v = e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0;
                    setBasicInfoDraft((p) => {
                      if (!p) return p;
                      return { ...p, unloadingStayingMinutes: v };
                    });
                    markBasicInfoDirty();
                  }}
                  slotProps={{
                    input: { endAdornment: <Typography variant="body2">分</Typography> },
                  }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium">
                  {draft.unloadingStayingMinutes === '' || draft.unloadingStayingMinutes === undefined
                    ? '—'
                    : `${draft.unloadingStayingMinutes}分`}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'teal.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            貨物条件
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">貨物種別</Typography>
              <Typography variant="body1" fontWeight="medium">
                {mergeUniqueFieldValues(orders, (o) => o.itemKlass)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">荷扱い</Typography>
              <Typography variant="body1" fontWeight="medium">
                {mergeUniqueFieldValues(orders, (o) => o.itemHandlingOfCargoStyle)}
              </Typography>
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle2" color="text.secondary">積み合わせ可否</Typography>
                <Tooltip title="積み合わせ不可の場合は、同じ荷主・同じ貨物種別の荷物としか運べません">
                  <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </Tooltip>
              </Box>
              <Typography variant="body1" fontWeight="medium">{mergeItemCanBeMixedLabel(orders)}</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'orange.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            備考
          </Typography>
          {/* issue #283: 備考は伝票ごとに異なる固有情報のため、レーン画面では閲覧のみ（編集不可） */}
          <Typography variant="body1" fontWeight="medium" sx={{ minHeight: '72px' }}>
            {memoDisplay || '—'}
          </Typography>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'lime.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            任意項目情報
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {/* issue #283: 任意項目は伝票ごとに異なる固有情報のため、レーン画面では閲覧のみ（編集不可） */}
            {customInputEntries.map((entry) => (
              <Box key={entry.key}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{entry.key}</Typography>
                <Typography variant="body1" fontWeight="medium">{entry.value || '—'}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 4, p: 3, bgcolor: 'purple.50', borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
            制約情報
          </Typography>
          <Box display="flex" flexDirection="column" gap={4}>
            <LaneConstraintGroup
              title="車両タイプ"
              category="klass"
              draftKey="designatedTruckKlasses"
              draft={draft}
              optionsOf={optionsOf}
              isLoading={attributeOptionsAreLoading}
              isError={attributeOptionsFailed}
              isEditing={isEditing}
              mergedValues={mergedDesignatedTruckKlasses}
              onToggle={toggleConstraint}
            />

            <LaneConstraintGroup
              title="車種"
              category="car_model"
              draftKey="designatedTruckCarModels"
              draft={draft}
              optionsOf={optionsOf}
              isLoading={attributeOptionsAreLoading}
              isError={attributeOptionsFailed}
              isEditing={isEditing}
              mergedValues={mergedDesignatedTruckCarModels}
              onToggle={toggleConstraint}
                nowrapLabel
            />

            <LaneConstraintGroup
              title="荷台高"
              category="loading_platform_height"
              draftKey="designatedTruckLoadingPlatformHeights"
              draft={draft}
              optionsOf={optionsOf}
              isLoading={attributeOptionsAreLoading}
              isError={attributeOptionsFailed}
              isEditing={isEditing}
              mergedValues={mergedDesignatedTruckLoadingPlatformHeights}
              onToggle={toggleConstraint}
            />

            <LaneConstraintGroup
              title="荷台幅"
              category="loading_platform_width"
              draftKey="designatedTruckLoadingPlatformWidths"
              draft={draft}
              optionsOf={optionsOf}
              isLoading={attributeOptionsAreLoading}
              isError={attributeOptionsFailed}
              isEditing={isEditing}
              mergedValues={mergedDesignatedTruckLoadingPlatformWidths}
              onToggle={toggleConstraint}
            />

            <LaneConstraintGroup
              title="荷台長"
              category="loading_platform_length"
              draftKey="designatedTruckLoadingPlatformLengths"
              draft={draft}
              optionsOf={optionsOf}
              isLoading={attributeOptionsAreLoading}
              isError={attributeOptionsFailed}
              isEditing={isEditing}
              mergedValues={mergedDesignatedTruckLoadingPlatformLengths}
              onToggle={toggleConstraint}
            />

            <LaneConstraintGroup
              title="床仕様"
              category="floor_specification"
              draftKey="designatedTruckFloorSpecifications"
              draft={draft}
              optionsOf={optionsOf}
              isLoading={attributeOptionsAreLoading}
              isError={attributeOptionsFailed}
              isEditing={isEditing}
              mergedValues={mergedDesignatedTruckFloorSpecifications}
              onToggle={toggleConstraint}
            />

            <LaneConstraintGroup
              title="装置"
              category="features"
              draftKey="designatedTruckFeatures"
              draft={draft}
              optionsOf={optionsOf}
              isLoading={attributeOptionsAreLoading}
              isError={attributeOptionsFailed}
              isEditing={isEditing}
              mergedValues={mergedDesignatedTruckFeatures}
              onToggle={toggleConstraint}
            />

            {isEditing ? (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TruckDriverConstraintsSelector
                  truckData={trucksOptions}
                  driverData={driversOptions}
                  allowedTrucks={draftAllowedTrucks}
                  deniedDrivers={draftDeniedDrivers}
                  onChangeAllowedTrucks={handleChangeAllowedTrucks}
                  onChangeDeniedDrivers={handleChangeDeniedDrivers}
                />
              </Box>
            ) : (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>指定トラック</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {allAllowedTruckIds.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">未指定</Typography>
                    ) : (
                      allAllowedTruckIds.map((id) => {
                        const t = trucksOptions.find((x) => x.id === id);
                        return (
                          <Chip key={id} label={t?.name ?? `ID:${id}`} size="small" color="success" variant="outlined" />
                        );
                      })
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>NGドライバー</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {allDeniedDriverIds.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">未指定</Typography>
                    ) : (
                      allDeniedDriverIds.map((id) => {
                        const d = driversOptions.find((x) => x.id === id);
                        return (
                          <Chip key={id} label={d?.name ?? `ID:${id}`} size="small" color="error" variant="outlined" />
                        );
                      })
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

/** 閲覧時: 複数伝票の和集合をマスタ順でマージし、読点区切りの1行で表示（例: 2t、4t、10t） */
const ConstraintChipRow: FC<{
  label?: string;
  values: string[];
}> = memo(({ label, values }) => {
  const uniq = [...new Set(values.filter(Boolean))];
  return (
    <Box>
      {label ? (
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>{label}</Typography>
      ) : null}
      {uniq.length === 0 ? (
        <Typography variant="body2" color="text.secondary">未指定</Typography>
      ) : (
        <Typography variant="body1" fontWeight="medium">{uniq.join('、')}</Typography>
      )}
    </Box>
  );
});

/**
 * 車両条件の 1 カテゴリ分。編集中はチェックボックス、閲覧中は伝票間マージ済みの 1 行。
 *
 * Why not 7 か所にチェックボックスの列挙を書き続ける: 読み込み中の分岐が 7 重複し、
 * どれか 1 つだけ直して残りが空の列を出す事故になる。見た目は元のまま 1 か所に寄せる。
 */
const EMPTY_VALUES: string[] = [];

/**
 * Why not options / onToggle を組み立てて渡す: 毎描画で新しい配列と新しい関数になるので、
 * memo が一度も効かない。カテゴリとドラフトのキーだけを渡し、組み立てはこの中で memo する。
 * 渡す側の `draft` と `optionsOf` / `onToggle` は参照が安定している。
 */
const LaneConstraintGroup: FC<{
  title: string;
  category: VehicleAttributeCategory;
  draftKey: ConstraintDraftKey;
  draft: LaneBasicInfoDraft;
  optionsOf: VehicleAttributeOptionsOf;
  /** 選択肢がまだ取れていない状態。空の行を出すと「選択肢が 0 件」に見える（#313・P3） */
  isLoading: boolean;
  /** 選択肢の取得に失敗した。現在値だけを出し、取得できなかったことを伝える */
  isError: boolean;
  isEditing: boolean;
  mergedValues: string[];
  onToggle: (key: ConstraintDraftKey, option: string, checked: boolean) => void;
  /** 車種だけ選択肢が多く 1 行に収まらないので、ラベル自体は折らない */
  nowrapLabel?: boolean;
}> = memo(({
  title, category, draftKey, draft, optionsOf,
  isLoading, isError, isEditing, mergedValues, onToggle, nowrapLabel = false,
}) => {
  const values = draft[draftKey] || EMPTY_VALUES;
  // ドラフトの現在値を混ぜる（設計書 §5-4）。値が変わったときだけ組み立て直す
  const options = useMemo(() => optionsOf(category, values), [optionsOf, category, values]);
  const handleToggle = useCallback(
    (option: string, checked: boolean) => onToggle(draftKey, option, checked),
    [onToggle, draftKey],
  );

  return (
  <Box>
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>{title}</Typography>
    {isEditing && (
      <VehicleAttributeOptionsState isLoading={isLoading} isError={isError}>
      <Box display="flex" gap={1} flexWrap="wrap">
        {options.map((option) => (
          <FormControlLabel
            key={option}
            control={(
              <Checkbox
                checked={values.includes(option)}
                onChange={(e) => handleToggle(option, e.target.checked)}
              />
            )}
            label={option}
            sx={nowrapLabel ? { whiteSpace: 'nowrap', minWidth: 'fit-content', flexShrink: 0, mb: 0 } : undefined}
          />
        ))}
      </Box>
      </VehicleAttributeOptionsState>
    )}
    {!isEditing && (
      // 閲覧のチップはマスタ順で並ぶので、揃う前に出すと到着の前後で並びが入れ替わって見える
      <VehicleAttributeOptionsState isLoading={isLoading} isError={isError}>
        <ConstraintChipRow values={mergedValues} />
      </VehicleAttributeOptionsState>
    )}
  </Box>
  );
});

export default LaneDetailBasicInfo;
